// CarWash.tsx
import React, { useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import CategoryTabs from "../home/components/CategoryTabs";
import ProductGrid from "../carwash/components/ProductGrid";
import { AddCarModal } from "../carwash/components/AddCarModal";
import OrderSummary from "../carwash/components/OrderSummary";
import { useCarWashData } from "../carwash/hooks/useCarWashData";
import { useMultiCarManagement } from "../carwash/hooks/useMultiCarManagement";
import { useCarWashActions } from "../carwash/hooks/useCarWashActions";
import CommonButtons from "src/shared/components/CommonButtons";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import "./CarWash.css";
import { useNavigate, useLocation } from "react-router-dom";
import { notifyWarning } from "src/assets/toastService";
import { useNavigationTracker } from "./useNavigationTracker";

const CarWash: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { categories, customerData, activeCategoryId, filteredProducts, customerId, handleCategoryChange, showAllProducts } = useCarWashData();

  const {
    multiCar,
    carNumber,
    setCarNumber,
    isModalOpen,
    setIsModalOpen,
    editCarIndex,
    isEditing,
    addCar,
    removeCar,
    removeProductFromCar,
    addProductToCar,
    calculateTotalPrice,
    startEditingCar,
    clearAllData,
    clearDataForPrint,
    clearDataForNavigationAway,
    markGoingToPayment,
    handleNavigateAway,
    shouldPreserveData,
  } = useMultiCarManagement(false);

  const { handleProductClick, handleNext, paymentMethod, handlePaymentMethodChange } = useCarWashActions({
    multiCar,
    editCarIndex,
    customerId,
    calculateTotalPrice,
    addProductToCar,
    setIsModalOpen,
    clearAllData: clearDataForPrint,
  });

  // Track navigation changes
  useNavigationTracker(handleNavigateAway);

  // Enhanced handleNext that marks going to payment
  const handleNextToPayment = () => {
    if (multiCar.length === 0) {
      toast.warning("Please add at least one car before proceeding.");
      return;
    }
    
    // Check that all cars have services
    const carsWithoutServices = multiCar.filter((car) => !car.productDetails || car.productDetails.length === 0);
    if (carsWithoutServices.length > 0) {
      const list = carsWithoutServices.map(car => car.carNumber || "Unnamed Car").join(", ");
      toast.warning(`Please select at least one service for: ${list}`);
      return;
    }
    
    // Check payment method
    if (!paymentMethod) {
      toast.warning("Please select a payment method before proceeding.");
      return;
    }
    
    // Mark that we're going to payment page
    markGoingToPayment();
    
    // Call the original handleNext
    handleNext();
  };

  const handleProductClickWithCarCheck = (product: any) => {
    if (multiCar.length === 0) {
      setIsModalOpen(true);
      notifyWarning("Please add a car number first before selecting products.");
    } else {
      handleProductClick(product);
    }
  };

  // Effect to handle beforeunload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (multiCar.length > 0) {
        sessionStorage.setItem("carWashNewSession", "true");
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [multiCar.length]);

  // Also track navigation using useEffect for immediate response
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Check if we're on a dashboard/order path and have car data
    const isDashboardPath = 
      currentPath.includes('/v1/new-order') ||
      currentPath.includes('/v1/CarDashboard') ||
      currentPath.includes('/v1/order-list') ||
      currentPath.includes('/v1/employee/new-order') ||
      currentPath.includes('/v1/employee/order-list') ||
      currentPath === '/';
    
    if (isDashboardPath && multiCar.length > 0) {
      // We navigated to dashboard with car data - clear it
      clearDataForNavigationAway();
    }
    
    document.title = "Car Wash › iMetanic";
  }, [location.pathname, multiCar.length, clearDataForNavigationAway]);

  // Handle back button click - check if we should preserve data
  const handleBackButton = () => {
    // Check if we're coming from payment
    if (shouldPreserveData) {
      // Don't clear data if coming back from payment
      window.history.back();
    } else {
      // Clear data for other back navigation
      clearAllData();
      window.history.back();
    }
  };

  // Show a toast if data was preserved from payment
  useEffect(() => {
    if (shouldPreserveData && multiCar.length > 0) {
      toast.info("Returned from payment page - order data preserved for editing");
    }
  }, [shouldPreserveData, multiCar.length]);

  return (
    <div>
      <div className="main-section">
        <div className="left-panel">
          <div className="flex items-center gap-2 mb-2">
            <CommonButtons
              className="p-3 transition-all duration-200 hover:scale-105"
              label="BACK"
              onClick={handleBackButton}
              variant="secondary"
              icon={KeyboardReturnIcon}
            />

        <p className="text-lg font-semibold text-blue-700 bg-blue-50 px-4 py-2 rounded-lg border border-blue-200 ml-8">
  Please Add car then select Category and Products
</p>


          </div>

          <div className="mb-8">
            <CategoryTabs categories={categories} activeCategoryId={activeCategoryId} onCategoryChange={handleCategoryChange} onShowAllProducts={showAllProducts} />
          </div>

          <ProductGrid products={filteredProducts} onProductClick={handleProductClickWithCarCheck} />
        </div>

        {/* Right Panel */}
        <OrderSummary
          Name={customerData?.Name}
          totalPrice={calculateTotalPrice()}
          multiCar={multiCar}
          isEditing={isEditing}
          editCarIndex={editCarIndex ?? 0}
          onEdit={startEditingCar}
          onDeleteCar={removeCar}
          onDeleteProduct={removeProductFromCar}
          onAddCar={() => setIsModalOpen(true)}
          onNext={handleNextToPayment}
          isNextDisabled={multiCar.length === 0}
          onPaymentMethodChange={handlePaymentMethodChange}
          selectedPaymentMethod={paymentMethod}
        />
      </div>

      <AddCarModal open={isModalOpen} carNumber={carNumber} onCarNumberChange={setCarNumber} onClose={() => setIsModalOpen(false)} onAdd={addCar} />

      <ToastContainer />
    </div>
  );
};

export default CarWash;